import type { Account } from '@adtrackify/at-tracking-event-types';
import { ListChecks, LogOut, PersonStanding, Plus, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';

import { AccountList } from '@/features/workspace/components/team-switcher/account-list';
import { AVATAR_FALLBACK_TONE, AVATAR_SIZE } from '@/components/ui/types/get-avatar-initials-enums';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { COMMAND_APPEARANCE } from '@/components/ui/types/command-variants-enums';
import { Command, CommandInput } from '@/components/ui/command';
import { CONTROL_SIZE } from '@/components/ui/types/control-enums';
import { Separator } from '@/components/ui/separator';
import {
  GET_STARTED_LABEL,
  GET_STARTED_PATH,
  SIGN_OUT_LABEL,
} from '@/lib/navigation/constants/account-action-catalog-constants';
import { cn } from '@/lib/utils';

export interface WorkspaceSwitcherContentProps {
  accounts: Account[];
  filteredAccounts: Account[];
  currentAccount: Account | null;
  isLoading: boolean;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onSelectAccount: (accountId: string) => void;
  onAddNewCompany: () => void;
  itemHeight: number;

  user: { displayName: string; email?: string };
  onSignOut: () => void;
  isSigningOut: boolean;
}

const menuItemClasses =
  'relative flex cursor-default select-none items-center gap-2 rounded-affordance px-2 py-1.5 type-body text-content-primary outline-none transition-[color,background-color] duration-standard ease-standard hover:bg-neutral-interaction-hover focus-visible:bg-neutral-interaction-hover pointer-coarse:min-h-11';

export function WorkspaceSwitcherContent({
  accounts,
  filteredAccounts,
  currentAccount,
  isLoading,
  searchQuery,
  onSearchChange,
  onSelectAccount,
  onAddNewCompany,
  itemHeight,
  user,
  onSignOut,
  isSigningOut,
}: WorkspaceSwitcherContentProps) {
  const useVirtual = accounts.length > 100;
  const displayAccounts = useVirtual ? filteredAccounts : accounts;

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col">
      {/* Header — Current workspace identity */}
      <div className="flex min-w-0 shrink-0 items-center gap-3 px-3 py-2.5">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-sidebar-primary text-sm font-semibold text-sidebar-primary-foreground">
          {currentAccount?.accountName?.charAt(0).toUpperCase() ?? 'W'}
        </div>
        <div className="min-w-0 flex-1">
          <div
            className="truncate type-body font-semibold text-content-primary"
            title={currentAccount?.accountName}
          >
            {currentAccount?.accountName ?? 'Select workspace'}
          </div>
        </div>
      </div>

      {/* Settings — grouped with header */}
      <div className="shrink-0 p-1">
        <Link to="/account/settings" className={cn(menuItemClasses, 'w-full')}>
          <Settings className="size-4 shrink-0" />
          Settings
        </Link>
        <Link to={GET_STARTED_PATH} className={cn(menuItemClasses, 'w-full')}>
          <ListChecks className="size-4 shrink-0" />
          {GET_STARTED_LABEL}
        </Link>
      </div>

      <Separator className="shrink-0" />

      {/* Workspace list scrolls; chrome below stays pinned */}
      <Command
        appearance={COMMAND_APPEARANCE.DEFAULT}
        shouldFilter={accounts.length <= 100}
        className="min-h-0 min-w-0 flex-1 overflow-hidden"
      >
        <CommandInput
          placeholder="Search workspaces..."
          value={searchQuery}
          onValueChange={onSearchChange}
          disabled={isLoading}
          size={CONTROL_SIZE.COMPACT}
        />
        <AccountList
          accounts={displayAccounts}
          currentAccountId={currentAccount?.id}
          isLoading={isLoading}
          useVirtual={useVirtual}
          itemHeight={itemHeight}
          onSelect={onSelectAccount}
        />
      </Command>

      {/* Add Workspace — grouped with workspace list */}
      <div className="shrink-0 p-1">
        <button
          type="button"
          className={cn(menuItemClasses, 'w-full')}
          disabled={isLoading}
          onClick={onAddNewCompany}
        >
          <Plus className="size-4 shrink-0" />
          Add a new workspace
        </button>
      </div>

      <Separator className="shrink-0" />

      {/* User Section */}
      <Link
        to="/account/settings/password"
        className={cn(menuItemClasses, 'w-full shrink-0 px-3 py-2')}
      >
        <Avatar size={AVATAR_SIZE.XS} className="shrink-0 border border-border-default">
          <AvatarFallback tone={AVATAR_FALLBACK_TONE.NEUTRAL}>
            <PersonStanding className="size-3" />
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="truncate type-body font-medium text-content-primary">
            {user.displayName}
          </div>
          {user.email ? (
            <div
              className="truncate type-settings-row-meta text-content-secondary"
              title={user.email}
            >
              {user.email}
            </div>
          ) : null}
        </div>
      </Link>

      {/* Sign Out — always pinned below the scrollable account list */}
      <div className="shrink-0 p-1">
        <button
          type="button"
          className={cn(
            menuItemClasses,
            'w-full disabled:pointer-events-none disabled:text-content-disabled disabled:opacity-100'
          )}
          disabled={isSigningOut}
          onClick={onSignOut}
        >
          <LogOut className="size-4 shrink-0" />
          {isSigningOut ? 'Signing out…' : SIGN_OUT_LABEL}
        </button>
      </div>
    </div>
  );
}
